import React, { useMemo } from 'react';
import Column from '../../../core/layout/Column';
import Row from '../../../core/layout/Row';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import MaskInput from '../../../core/form/MaskInput';
import Select from '../../../core/form/Select';
import Table from '../../../core/form/Table';
import Button from '../../../core/form/Button';
import {
  CONTRACT_PRODUCTS,
  CURRENCY_MASK_OPTIONS,
  HS_SKU_PRODUCTS_CAN_BE_REMOVED_OR_ADDED_ON_EDIT,
  HS_SKU_PRODUCTS_CAN_USE_FREE_AMOUNT,
  HS_SKU_PRODUCTS_CAN_USE_INSTALLMENTS,
} from '../../../core/consts';
import Dialog from '../../../core/layout/Dialog';
import FormButtons from '../../../core/form/FormButtons';
import TextInput from '../../../core/form/TextInput';
import Switch from '../../../core/form/Switch';

const ContractProductsModal = (props) => {
  const disableInstallments = !HS_SKU_PRODUCTS_CAN_USE_INSTALLMENTS.includes(props.values.hs_sku);
  const disableFreeAmount = !HS_SKU_PRODUCTS_CAN_USE_FREE_AMOUNT.includes(props.values.hs_sku);
  const realCurrencyFormatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
  const dollarCurrencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

  const productsOptions = useMemo(() => {
    if (props.hideCoreProducts) {
      const productsToShow = [...HS_SKU_PRODUCTS_CAN_BE_REMOVED_OR_ADDED_ON_EDIT];
      if (props.values.hs_sku) {
        productsToShow.push(props.values.hs_sku);
      }
      return [...props.options].filter((option) => productsToShow.includes(option.value));
    }
    return [...props.options];
  }, [props.options, props.hideCoreProducts, props.values]);

  const useV2Form = useMemo(() => {
    if (props.isEditingContract) {
      return props.type === 'UPFRONT' && props.isV2;
    } else {
      return props.type === 'UPFRONT';
    }
  }, [props.type, props.isV2, props.isEditingContract]);

  return (
    <Dialog
      title={props.isEditingProduct ? 'Edit Product' : 'Add Product'}
      isOpen={props.isModalOpen}
      onCancel={props.onClose}
      actions={null}
      width="35%"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          props.onSubmit();
        }}
      >
        {!useV2Form && (
          <>
            <Row>
              <Column size={12}>
                <Select
                  onChange={(value) => {
                    props.onReset();
                    props.onChange('hs_sku', value);
                  }}
                  options={productsOptions}
                  label="Product"
                  required
                  disabled={
                    props.submitting ||
                    props.loading ||
                    props.disabled ||
                    (props.hideCoreProducts &&
                      get(props.values, 'hs_sku') &&
                      !HS_SKU_PRODUCTS_CAN_BE_REMOVED_OR_ADDED_ON_EDIT.includes(get(props.values, 'hs_sku')))
                  }
                  value={get(props.values, 'hs_sku', null)}
                  errorText={get(props.errors, 'hs_sku', '')}
                  showSearch
                  filterOption={(input, option) =>
                    option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                />
              </Column>
            </Row>
            {![CONTRACT_PRODUCTS.EXTRA_LICENSES_RANGE, CONTRACT_PRODUCTS.EXTRA_LICENSES_SEAT_RANGE].includes(
              props.values.hs_sku
            ) ? (
              <>
                {get(props.values, 'hs_sku') && get(props.values, 'hs_sku', null) === 'others' && (
                  <Row>
                    <Column size={12}>
                      <TextInput
                        label="Description"
                        value={get(props.values, 'description', null)}
                        onChange={(value) => props.onChange('description', value)}
                        disabled={get(props.values, 'hs_sku', '') !== 'others'}
                        errorText={get(props.errors, 'description', '')}
                      />
                    </Column>
                  </Row>
                )}
                <Row>
                  <Column size={3}>
                    <MaskInput
                      disabled={props.submitting || props.loading || props.disabled}
                      label="Amount"
                      value={get(props.values, 'amount', null)}
                      onChange={(value) => props.onChange('amount', value)}
                      blocks={[5]}
                      numericOnly
                    />
                  </Column>
                  <Column size={3}>
                    <MaskInput
                      disabled={props.submitting || props.loading || props.disabled || disableFreeAmount}
                      label="Free Amount"
                      value={get(props.values, 'freeAmount', null)}
                      onChange={(value) => props.onChange('freeAmount', value)}
                      blocks={[5]}
                      numericOnly
                    />
                  </Column>
                  <Column size={3}>
                    <MaskInput
                      disabled={props.submitting || props.loading || props.disabled}
                      label={`Final Unit Price (${CURRENCY_MASK_OPTIONS[props.currencyType].symbol})`}
                      required
                      value={get(props.values, 'finalUnitPrice', null)}
                      onChange={(value) => props.onChange('finalUnitPrice', value)}
                      errorText={get(props.errors, 'finalUnitPrice', '')}
                      delimiter={CURRENCY_MASK_OPTIONS[props.currencyType].delimiter}
                      numeralDecimalMark={CURRENCY_MASK_OPTIONS[props.currencyType].numeralDecimalMark}
                      maskType="numeral"
                    />
                  </Column>
                </Row>
                <Row>
                  <Column size={3}>
                    <MaskInput
                      disabled={props.submitting || props.loading || props.disabled || disableInstallments}
                      label="Installments"
                      value={get(props.values, 'installments', null)}
                      onChange={(value) => props.onChange('installments', value)}
                      blocks={[2]}
                      numericOnly
                    />
                  </Column>
                  <Column size={3}>
                    <MaskInput
                      disabled={props.submitting || props.loading || props.disabled || disableInstallments}
                      label="Paid Installments"
                      value={get(props.values, 'paidInstallments', null)}
                      onChange={(value) => props.onChange('paidInstallments', value)}
                      blocks={[2]}
                      numericOnly
                    />
                  </Column>
                </Row>
              </>
            ) : (
              <>
                <Row>
                  <Column size={3}>
                    <MaskInput
                      disabled={props.submitting || props.loading || props.disabled}
                      label="Min"
                      value={get(props.values, 'min', null)}
                      onChange={(value) => props.onChange('min', value)}
                      blocks={[11]}
                      numericOnly
                      required
                    />
                  </Column>
                  <Column size={3}>
                    <MaskInput
                      disabled={props.submitting || props.loading || props.disabled}
                      label="Max"
                      value={get(props.values, 'max', null)}
                      onChange={(value) => props.onChange('max', value)}
                      blocks={[11]}
                      numericOnly
                      required
                    />
                  </Column>
                  <Column size={3}>
                    <MaskInput
                      disabled={props.submitting || props.loading || props.disabled}
                      label={`Price (${CURRENCY_MASK_OPTIONS[props.currencyType].symbol})`}
                      value={get(props.values, 'rangePrice', null)}
                      onChange={(value) => props.onChange('rangePrice', value)}
                      errorText={get(props.errors, 'finalUnitPriceRanges', '')}
                      delimiter={CURRENCY_MASK_OPTIONS[props.currencyType].delimiter}
                      numeralDecimalMark={CURRENCY_MASK_OPTIONS[props.currencyType].numeralDecimalMark}
                      maskType="numeral"
                      required
                    />
                  </Column>
                  <Column size={3}>
                    <div style={{ height: 44 }} />
                    <Button
                      icon="plus"
                      label="Add"
                      buttonType="button"
                      onClick={props.onAddRange}
                      disabled={props.submitting || props.loading || props.disabled}
                    />
                  </Column>
                </Row>
                <Row>
                  <Table
                    columns={[
                      {
                        label: 'Min',
                        path: 'min',
                      },
                      {
                        label: 'Max',
                        path: 'max',
                      },
                      {
                        label: 'Price',
                        path: 'price',
                        render: (cell) => {
                          return props.currencyType === 'real'
                            ? realCurrencyFormatter.format(cell)
                            : dollarCurrencyFormatter.format(cell);
                        },
                      },
                      {
                        label: 'Action',
                        path: 'action',
                        render: (value, row, index) =>
                          props.values.finalUnitPriceRanges &&
                          index === props.values.finalUnitPriceRanges.length - 1 ? (
                            <Button icon="delete" onClick={() => props.onDeleteRange(row)} />
                          ) : null,
                      },
                    ]}
                    rows={props.values.finalUnitPriceRanges || []}
                  />
                </Row>
              </>
            )}
          </>
        )}

        {useV2Form && (
          <>
            <Row>
              <Column size={12}>
                <Select
                  onChange={(value) => {
                    props.onReset();
                    props.onChange('hs_sku', value);
                  }}
                  options={productsOptions.filter((option) => {
                    if (!props.isEditingProduct) {
                      return option.value === CONTRACT_PRODUCTS.OTHERS;
                    }
                    return true;
                  })}
                  label="Product"
                  required
                  disabled={
                    props.submitting ||
                    props.loading ||
                    props.disabled ||
                    [
                      CONTRACT_PRODUCTS.CUSTOM_PACKAGE,
                      CONTRACT_PRODUCTS.PLACEMENT_TEST_COMERCIAL,
                      CONTRACT_PRODUCTS.TEACHERS_WEB_PAGE,
                      'feature-video-lessons',
                    ].includes(get(props.values, 'hs_sku', ''))
                  }
                  value={get(props.values, 'hs_sku', null)}
                  errorText={get(props.errors, 'hs_sku', '')}
                  showSearch
                  filterOption={(input, option) =>
                    option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                />
              </Column>
            </Row>
            {get(props.values, 'hs_sku', '') === CONTRACT_PRODUCTS.OTHERS && (
              <Row>
                <Column size={12}>
                  <TextInput
                    label="Description"
                    value={get(props.values, 'description', null)}
                    onChange={(value) => props.onChange('description', value)}
                    disabled={get(props.values, 'hs_sku', '') !== 'others'}
                    errorText={get(props.errors, 'description', '')}
                  />
                </Column>
              </Row>
            )}
            <Row>
              <Column size={3}>
                <MaskInput
                  disabled={
                    props.submitting ||
                    props.loading ||
                    props.disabled ||
                    [CONTRACT_PRODUCTS.PLACEMENT_TEST_COMERCIAL, CONTRACT_PRODUCTS.TEACHERS_WEB_PAGE].includes(
                      get(props.values, 'hs_sku', '')
                    )
                  }
                  label="Amount"
                  value={get(props.values, 'amount', null)}
                  onChange={(value) => {
                    props.onChange('amount', value);
                    props.onChange('amount', value);
                  }}
                  blocks={[5]}
                  numericOnly
                />
              </Column>
              <Column size={3}>
                <MaskInput
                  disabled={props.submitting || props.loading || props.disabled || disableFreeAmount}
                  label="Free Amount"
                  value={get(props.values, 'freeAmount', null)}
                  onChange={(value) => props.onChange('freeAmount', value)}
                  blocks={[5]}
                  numericOnly
                />
              </Column>
              <Column size={3}>
                <MaskInput
                  disabled={
                    props.submitting ||
                    props.loading ||
                    get(props.values, 'hs_sku', '') === CONTRACT_PRODUCTS.CUSTOM_PACKAGE
                  }
                  label="Unit Price"
                  required
                  value={get(props.values, 'finalUnitPrice', null)}
                  onChange={(value) => props.onChange('finalUnitPrice', value)}
                  errorText={get(props.errors, 'finalUnitPrice', '')}
                  delimiter={CURRENCY_MASK_OPTIONS.real.delimiter}
                  numeralDecimalMark={CURRENCY_MASK_OPTIONS.real.numeralDecimalMark}
                  maskType="numeral"
                />
              </Column>
              {get(props.values, 'hs_sku', '') === CONTRACT_PRODUCTS.CUSTOM_PACKAGE && (
                <Column size={3}>
                  <MaskInput
                    disabled={props.submitting || props.loading}
                    label="Discount (%)"
                    required
                    value={get(props.values, 'discount', null)}
                    onChange={(value) => props.onChange('discount', value)}
                    errorText={get(props.errors, 'discount', '')}
                    numeralDecimalMark=","
                    delimiter=""
                    numeralDecimalScale={4}
                    numeralPositiveOnly
                    maskType="numeral"
                  />
                </Column>
              )}
            </Row>
            {get(props.values, 'hs_sku', '') === CONTRACT_PRODUCTS.OTHERS && (
              <Row>
                <Column size={3}>
                  <MaskInput
                    disabled={props.submitting || props.loading || props.disabled || disableInstallments}
                    label="Installments"
                    value={get(props.values, 'installments', null)}
                    onChange={(value) => props.onChange('installments', value)}
                    blocks={[2]}
                    numericOnly
                  />
                </Column>
                <Column size={3}>
                  <MaskInput
                    disabled={props.submitting || props.loading || props.disabled || disableInstallments}
                    label="Paid Installments"
                    value={get(props.values, 'paidInstallments', null)}
                    onChange={(value) => props.onChange('paidInstallments', value)}
                    blocks={[2]}
                    numericOnly
                  />
                </Column>
                <Column size={4}>
                  <div style={{ height: '40px', marginLeft: '10px' }}>
                    <Switch
                      label="Is Debit Product?"
                      value={get(props.values, 'isDebitProduct', false)}
                      onChange={() => {
                        props.onChange('isDebitProduct', !get(props.values, 'isDebitProduct', false));
                      }}
                      disabled={props.submitting || props.loading || props.disabled}
                    />
                  </div>
                </Column>
              </Row>
            )}
          </>
        )}

        <FormButtons
          confirmLabel={`${props.isEditingProduct ? 'Update' : 'Create'} Product`}
          isDisabled={props.submitting || props.loading || props.disabled}
          isSubmitting={props.submitting}
          onReset={props.onReset}
        />
      </form>
    </Dialog>
  );
};

ContractProductsModal.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      hs_sku: PropTypes.string,
      name: PropTypes.string,
      price: PropTypes.number,
    })
  ),
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onAddRange: PropTypes.func.isRequired,
  onDeleteRange: PropTypes.func.isRequired,
  values: PropTypes.object,
  errors: PropTypes.object,
  disabled: PropTypes.bool,
  submitting: PropTypes.bool,
  loading: PropTypes.bool,
  currencyType: PropTypes.oneOf(['real', 'dollar']),
  isModalOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  isEditing: PropTypes.bool,
  hideCoreProducts: PropTypes.bool,
  type: PropTypes.oneOf(['UPFRONT', 'POST_PAYMENT']),
};

ContractProductsModal.defaultProps = {
  values: {},
  errors: {},
  submitting: false,
  loading: false,
  currencyType: 'real',
  isEditing: false,
  isDebitProduct: false,
  hideCoreProducts: false,
};

export default ContractProductsModal;
